<script setup>
/**
 * @description 商城一级分类 PK 页面
 * @reason 按设计稿在一级分类首页展示二级分类入口、商品选择与三列参数对比
 */

import { BaseMessage } from '~/utils/baseMessage'
import { computed, onMounted, onServerPrefetch, shallowRef, ref, watch, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { aggregatePageApi } from '~/api/common/aggregate-page'
import { useCategoryStore } from '~/store/common/category'
import { useStoreCartStore } from '~/store/store/cart'
import { storeApi } from '@/api/store'
import { useUserStore } from '~/store/common/user'
import {
  buildAggregateCategoryPkSections,
  normalizeAggregatePagePayload,
} from '~/utils/store/aggregatePage'
import { buildListAddToCartPayload } from '~/utils/product'
import { DISPLAY_PAGE } from '@/utils/constant'
import BaseSkeleton from '~/components/base/Skeleton.vue'
import BaseCardCarousel from '~/components/base/CardCarousel.vue'
import StoreHomeBanner from '~/components/store/home/Banner.vue'
import StoreHomeCategoryNav from '~/components/store/home/CategoryNav.vue'
import StoreCategoryPkProductCard from '~/components/store/categoryPk/ProductCard.vue'
import StoreCategoryPkComparisonTable from '~/components/store/categoryPk/ComparisonTable.vue'
import { createPublicContentSsrRequestConfig } from '~/utils/website/ssrRequestPolicy'
import { usePageTracking, trackClickEvent } from '~/composables/usePageTracking'
import {
  buildCategoryBannerTracking,
  buildCategoryPageTracking,
} from '~/utils/tracking/pageRegistry'
import { buildProductTrackFields, resolveCategoryTrackingPrefix } from '~/utils/tracking/helpers'
import {
  buildAddToCartTrack,
  buildCategoryPkTrackEvents,
  buildProductClickPayload,
} from '~/utils/tracking/eventBuilders'
import {
  hasExposedInPage,
  markExposedInPage,
  safeTrack,
  useViewportExposure,
} from '~/utils/tracker'

definePageMeta({
  layout: 'store',
})

const route = useRoute()
const categoryStore = useCategoryStore()
const cartStore = useStoreCartStore()
const userStore = useUserStore()
const publicContentSsrEnabled = usePublicContentSsrEnabled()
const { isMobile } = useMediaQuery()

const loading = ref(true)
const parameterLoading = ref(false)
const aggregatePageLoadFailed = ref(false)
const displayProducts = shallowRef([])
const aggregatePage = shallowRef(null)
const aggregatePageCategoryId = ref('')
const selectedPkProducts = shallowRef([null, null, null])
const pkProductDetailMap = shallowRef(new Map())
let parameterRequestToken = 0
let categoryContentRequestToken = 0

// SSR 以受限的访客请求预取分类；客户端挂载后会按当前状态重新拉取。
onMounted(() => {
  categoryStore.fetchCategoryInfo().catch((error) => {
    console.error('Failed to fetch category info:', error)
    BaseMessage.error('分类数据加载失败')
  })
})

const routeCategoryId = computed(() => normalizeRouteId(route.params.id))
const currentPrimaryCategory = computed(
  () =>
    categoryStore.categoryList.find(
      (item) => String(item.categoryId) === String(routeCategoryId.value)
    ) || null
)

const pageTitle = computed(
  () =>
    currentPrimaryCategory.value?.categoryName ||
    aggregatePage.value?.pageName ||
    aggregatePage.value?.categoryName ||
    '分类产品'
)

const categoryTrackingPrefix = computed(() => {
  const index = categoryStore.categoryList.findIndex(
    (item) => String(item.categoryId) === String(routeCategoryId.value)
  )
  return resolveCategoryTrackingPrefix(currentPrimaryCategory.value, Math.max(index, 0))
})

const categoryPageTracking = computed(() =>
  buildCategoryPageTracking(categoryTrackingPrefix.value, routeCategoryId.value, pageTitle.value)
)

const categoryBannerTracking = computed(() =>
  buildCategoryBannerTracking(categoryTrackingPrefix.value, pageTitle.value)
)

const categoryNavTracking = computed(() => ({
  categoryClickEvent: `pv_${categoryTrackingPrefix.value}_Category_click`,
  pageName: pageTitle.value,
}))

const categoryPkEvents = computed(() => buildCategoryPkTrackEvents(categoryTrackingPrefix.value))

const categoryProductTrackContext = computed(() => ({
  pageName: pageTitle.value,
  clickEvent: categoryPkEvents.value.productCardClick,
  addToCartEvent: categoryPkEvents.value.addToCart,
  fromPage: 'category_homepage',
  sourceType: 'category_homepage',
  sourceId: String(activeSecondaryCategoryId.value || routeCategoryId.value || ''),
  sourceName: pageTitle.value,
}))

const categoryCardExposure = useViewportExposure({ threshold: 0.5 })

usePageTracking(categoryPageTracking)
const aggregateSecondaryCategories = computed(() => {
  return buildAggregateCategoryPkSections(aggregatePage.value).map((section) => ({
    ...section,
    pic: categoryStore.findCategoryById(section.categoryId)?.pic || section.pic,
  }))
})
const secondaryCategories = computed(() => aggregateSecondaryCategories.value)
const activeSecondaryCategory = computed(() => {
  return secondaryCategories.value.find(
    (item) => String(item.categoryId) === String(activeSecondaryCategoryId.value)
  )
})
const activeSecondaryCategoryId = ref('')
const categoryPageId = computed(
  () => currentPrimaryCategory.value?.categoryId || routeCategoryId.value || null
)
const shouldUseAutoPkOnly = computed(() => {
  return displayProducts.value.length > 0 && displayProducts.value.length <= 3
})
const shouldShowProductPicker = computed(
  () => displayProducts.value.length > 3 || (isMobile.value && displayProducts.value.length > 0)
)

const displayPageTitle = computed(
  () => activeSecondaryCategory.value?.categoryName || pageTitle.value
)

useSeoMeta({
  title: () => `${pageTitle.value} - 腾达(Tenda) 官方网站`,
  description: () => `浏览 ${pageTitle.value} 产品并进行参数对比，快速选择适合您的网络设备。`,
})

/**
 * @description 标准化路由参数为单个字符串
 * @param {string|string[]|number|null|undefined} value - 路由参数
 * @returns {string}
 */
function normalizeRouteId(value) {
  if (Array.isArray(value)) return value[0] ? String(value[0]) : ''
  return value ? String(value) : ''
}

/**
 * @description 根据聚合页分类配置和路由参数解析当前二级分类
 * @param {string|string[]|number|null|undefined} secondaryQueryId - 路由二级分类 ID
 * @returns {string}
 */
function resolveActiveSecondaryCategoryId(secondaryQueryId) {
  const normalizedQueryId = normalizeRouteId(secondaryQueryId)
  const children = aggregateSecondaryCategories.value
  const hasQueryCategory = children.some(
    (item) => String(item.categoryId) === String(normalizedQueryId)
  )
  const fallbackId = children[0]?.categoryId || ''
  if (!fallbackId) return ''
  return hasQueryCategory ? normalizedQueryId : String(fallbackId)
}

/**
 * @description 获取商品 ID
 * @param {Object} product - 商品数据
 * @returns {string|number}
 */
function getProductId(product) {
  return product?.prodId || product?.id
}

/**
 * @description 判断商品是否已在 PK 区
 * @param {string|number} productId - 商品 ID
 * @returns {boolean}
 */
function isProductSelected(productId) {
  return selectedPkProducts.value.some((item) => String(getProductId(item)) === String(productId))
}

/**
 * @description 判断商品是否为排序第一的固定基准商品
 * @param {Object} product - 商品数据
 * @param {number} index - 商品排序
 * @returns {boolean}
 */
function isAnchorProduct(product, index) {
  const firstProduct = displayProducts.value[0]
  return index === 0 && String(getProductId(product)) === String(getProductId(firstProduct))
}

/**
 * @description 判断对象是否为参数项节点
 * @param {Object|null|undefined} item - 待判断对象
 * @returns {boolean}
 */
function isParameterItem(item) {
  return Boolean(
    item &&
    typeof item === 'object' &&
    (item.paramName || item.paramValue || Array.isArray(item.params))
  )
}

/**
 * @description 标准化后端商品参数分组，兼容商详 parameters 与历史静态数据字段。
 * @param {Array|Object|null|undefined} groups - 原始参数分组
 * @returns {Array}
 */
function normalizeProductParameterGroups(groups) {
  if (!groups) return []
  const sourceGroups = Array.isArray(groups)
    ? groups.some(isParameterItem)
      ? [{ key: 'parameters', items: groups }]
      : groups
    : typeof groups === 'object'
      ? [groups]
      : []

  return sourceGroups
    .map((group, groupIndex) => {
      const items = Array.isArray(group?.items)
        ? group.items
        : Array.isArray(group?.params)
          ? group.params
          : isParameterItem(group)
            ? [group]
            : []

      return {
        ...group,
        key: group?.key || group?.groupName || group?.paramName || `parameter-group-${groupIndex}`,
        items,
      }
    })
    .filter((group) => group.items.length > 0)
}

/**
 * @description 从商品详情缓存中查找当前商品的真实参数分组
 * @param {Object} product - 商品数据
 * @returns {Array}
 */
function getProductParameterGroups(product) {
  const productId = getProductId(product)
  const detail = productId ? pkProductDetailMap.value.get(String(productId)) : null
  const detailGroups = normalizeProductParameterGroups(detail?.parameters)
  if (detailGroups.length > 0) return detailGroups

  return normalizeProductParameterGroups(product?.parameters)
}

/**
 * @description 统一清洗参数文本
 * @param {string|number|null|undefined} value - 原始值
 * @returns {string}
 */
function normalizeText(value) {
  return String(value ?? '').trim()
}

/**
 * @description 创建参数树节点状态
 * @param {string} label - 当前节点名称
 * @param {string} key - 当前节点唯一键
 * @param {number} productsLength - 对比商品数量
 * @param {number} order - 当前层排序
 * @returns {Object}
 */
function createParameterNodeState(label, key, productsLength, order) {
  return {
    key,
    label,
    order,
    values: Array(productsLength).fill(''),
    children: [],
    childrenMap: new Map(),
  }
}

/**
 * @description 将多层参数递归合并为可渲染的对比树
 * @param {Object} parentState - 父级树状态
 * @param {Array} items - 当前层参数
 * @param {number} productIndex - 当前商品索引
 * @param {number} productsLength - 商品总数
 * @param {string[]} path - 当前路径
 * @returns {void}
 */
function mergeParameterItemsIntoTree(parentState, items, productIndex, productsLength, path = []) {
  const sourceItems = Array.isArray(items) ? items : []

  sourceItems.forEach((item, itemIndex) => {
    const itemName = normalizeText(item?.paramName)
    const itemValue = normalizeText(item?.paramValue)
    const childParams = Array.isArray(item?.params) ? item.params : []

    if (!itemName) {
      if (childParams.length > 0) {
        mergeParameterItemsIntoTree(parentState, childParams, productIndex, productsLength, path)
      }
      return
    }

    const currentPath = [...path, itemName]
    const nodeKey = currentPath.join('::') || `category-pk-parameter-${itemIndex}`
    let nodeState = parentState.childrenMap.get(itemName)

    if (!nodeState) {
      nodeState = createParameterNodeState(
        itemName,
        nodeKey,
        productsLength,
        parentState.children.length
      )
      parentState.childrenMap.set(itemName, nodeState)
      parentState.children.push(nodeState)
    }

    if (childParams.length > 0) {
      mergeParameterItemsIntoTree(nodeState, childParams, productIndex, productsLength, currentPath)
      return
    }

    nodeState.values[productIndex] = itemValue
  })
}

/**
 * @description 标准化参数树节点
 * @param {Array} nodes - 参数树节点
 * @returns {Array}
 */
function finalizeParameterNodes(nodes) {
  return (Array.isArray(nodes) ? nodes : [])
    .slice()
    .sort((firstNode, secondNode) => firstNode.order - secondNode.order)
    .map((node) => {
      const children = finalizeParameterNodes(node.children)
      const isCategory = children.length > 0

      return {
        key: node.key,
        label: node.label,
        values: isCategory ? Array(node.values.length).fill('') : node.values.map(normalizeText),
        isCategory,
        children,
      }
    })
}

/**
 * @description 将参数树拍平成表格行，并保留层级信息
 * @param {Array} nodes - 参数树节点
 * @param {number} level - 当前层级
 * @returns {Array}
 */
function flattenParameterRows(nodes, level = 0) {
  const sourceNodes = Array.isArray(nodes) ? nodes : []

  return sourceNodes.flatMap((node, index) => [
    {
      key: node.key,
      label: node.label,
      values: node.values,
      level,
      isCategory: node.isCategory,
      isLastSibling: index === sourceNodes.length - 1,
    },
    ...flattenParameterRows(node.children, level + 1),
  ])
}

const parameterRows = computed(() => {
  const products = selectedPkProducts.value
  const rootState = {
    children: [],
    childrenMap: new Map(),
  }

  products.forEach((product, productIndex) => {
    if (!product) return

    getProductParameterGroups(product).forEach((group) => {
      mergeParameterItemsIntoTree(
        rootState,
        Array.isArray(group?.items) ? group.items : [],
        productIndex,
        products.length
      )
    })
  })

  // 首层是参数组容器，只用于聚合，不作为可见对比行展示。
  return finalizeParameterNodes(rootState.children).flatMap((node) =>
    node.isCategory ? flattenParameterRows(node.children, 0) : flattenParameterRows([node], 0)
  )
})

function getMissingParameterProducts(products) {
  return (Array.isArray(products) ? products : []).filter((product) => {
    const productId = getProductId(product)
    if (!productId) return false
    if (pkProductDetailMap.value.has(String(productId))) return false
    return normalizeProductParameterGroups(product?.parameters).length === 0
  })
}

async function ensurePkProductParameters(products, requestConfig = {}) {
  const validProducts = (Array.isArray(products) ? products : []).filter((p) => getProductId(p))
  const requestToken = ++parameterRequestToken
  if (validProducts.length === 0) {
    parameterLoading.value = false
    return
  }

  // 已有缓存的商品跳过
  const missingProducts = validProducts.filter(
    (p) => !pkProductDetailMap.value.has(String(getProductId(p)))
  )
  if (missingProducts.length === 0) {
    parameterLoading.value = false
    return
  }

  parameterLoading.value = true
  try {
    const ids = missingProducts.map((p) => Number(getProductId(p)))
    const result = await storeApi.compareProducts({ productIds: ids }, requestConfig)
    if (requestToken !== parameterRequestToken) return

    const list = Array.isArray(result?.products) ? result.products : []
    const nextMap = new Map(pkProductDetailMap.value)
    for (const p of list) {
      nextMap.set(String(p.id), p)
    }
    pkProductDetailMap.value = nextMap
  } catch (error) {
    console.error('获取对比参数失败:', error)
  } finally {
    if (requestToken === parameterRequestToken) {
      parameterLoading.value = false
    }
  }
}

/**
 * @description 从当前聚合页二级分类区块读取后台配置的 PK 商品
 * @reason PK 商品由聚合页后台逐分类配置，不能用分类搜索结果覆盖
 * @returns {Promise<void>}
 */
async function fetchCategoryProducts(
  requestToken = categoryContentRequestToken,
  requestConfig = {}
) {
  const activeSection = aggregateSecondaryCategories.value.find(
    (section) => String(section.categoryId) === String(activeSecondaryCategoryId.value)
  )
  if (!activeSection) {
    if (requestToken !== categoryContentRequestToken) return
    parameterRequestToken += 1
    parameterLoading.value = false
    displayProducts.value = []
    selectedPkProducts.value = [null, null, null]
    loading.value = false
    return
  }

  loading.value = true
  try {
    if (requestToken !== categoryContentRequestToken) return
    const products = Array.isArray(activeSection.products) ? activeSection.products : []

    displayProducts.value = products
    selectedPkProducts.value = [products[0] || null, products[1] || null, products[2] || null]
    await ensurePkProductParameters(selectedPkProducts.value, requestConfig)
    loading.value = false
  } catch (error) {
    if (requestToken !== categoryContentRequestToken) return
    console.error('Failed to fetch category PK products:', error)
    loading.value = false
    if (import.meta.client) {
      BaseMessage.error('商品数据加载失败')
    }
  }
}

async function fetchCategoryAggregatePage(
  requestToken = categoryContentRequestToken,
  requestConfig = {}
) {
  const categoryId = currentPrimaryCategory.value?.categoryId || routeCategoryId.value
  if (!categoryId) {
    if (requestToken !== categoryContentRequestToken) return
    aggregatePage.value = null
    aggregatePageCategoryId.value = ''
    aggregatePageLoadFailed.value = true
    return false
  }

  try {
    const response = await aggregatePageApi.getCategoryPage(
      categoryId,
      {
        salesOrganization: 'TDC',
      },
      requestConfig
    )
    if (requestToken !== categoryContentRequestToken) return
    const normalizedPage = normalizeAggregatePagePayload(response)
    if (!normalizedPage) {
      throw new Error('分类聚合页返回空数据')
    }

    const normalizedCategoryId = String(categoryId)
    aggregatePage.value = normalizedPage
    aggregatePageCategoryId.value = normalizedCategoryId
    aggregatePageLoadFailed.value = false
    return true
  } catch (error) {
    if (requestToken !== categoryContentRequestToken) return
    const normalizedCategoryId = String(categoryId)
    const canRetainCurrentPage =
      aggregatePage.value && aggregatePageCategoryId.value === normalizedCategoryId
    if (canRetainCurrentPage) {
      aggregatePageLoadFailed.value = false
      console.warn(`分类 ${normalizedCategoryId} 聚合页刷新失败，继续使用最近一次成功数据`, error)
      return true
    }

    aggregatePage.value = null
    aggregatePageCategoryId.value = ''
    aggregatePageLoadFailed.value = true
    console.error('Failed to fetch category aggregate page:', error)
    return false
  }
}

async function refreshCategoryContent(requestConfig = {}, { preserveExisting = false } = {}) {
  const requestToken = ++categoryContentRequestToken
  const targetCategoryId = String(
    currentPrimaryCategory.value?.categoryId || routeCategoryId.value || ''
  )
  const canPreserveExisting =
    preserveExisting &&
    aggregatePage.value &&
    (!aggregatePageCategoryId.value || aggregatePageCategoryId.value === targetCategoryId)

  loading.value = true
  aggregatePageLoadFailed.value = false
  parameterRequestToken += 1
  parameterLoading.value = false
  if (!canPreserveExisting) {
    aggregatePage.value = null
    aggregatePageCategoryId.value = ''
    displayProducts.value = []
    selectedPkProducts.value = [null, null, null]
  }

  const hasAggregatePage = await fetchCategoryAggregatePage(requestToken, requestConfig)
  if (requestToken !== categoryContentRequestToken) return
  if (!hasAggregatePage) {
    loading.value = false
    return
  }

  activeSecondaryCategoryId.value = resolveActiveSecondaryCategoryId(
    route.query.secondaryCategoryId
  )

  await fetchCategoryProducts(requestToken, requestConfig)
}

async function retryCategoryContent() {
  await refreshCategoryContent({}, { preserveExisting: true })
}

function replaceSecondaryCategoryQuery(categoryId) {
  if (!import.meta.client) return

  const url = new URL(window.location.href)
  if (categoryId === null || categoryId === undefined || categoryId === '') {
    url.searchParams.delete('secondaryCategoryId')
  } else {
    url.searchParams.set('secondaryCategoryId', String(categoryId))
  }
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`)
}

async function refreshCategoryProductsOnly() {
  const requestToken = ++categoryContentRequestToken
  loading.value = true
  parameterRequestToken += 1
  parameterLoading.value = false
  displayProducts.value = []
  selectedPkProducts.value = [null, null, null]

  await fetchCategoryProducts(requestToken)
}

/**
 * @description 添加商品到 PK 的第一个空列
 * @param {Object} product - 商品数据
 * @returns {void}
 */
function addProductToPk(product) {
  if (!product || isProductSelected(getProductId(product))) return
  const nextProducts = [...selectedPkProducts.value]
  const emptyIndex = nextProducts.findIndex((item) => !item)

  if (emptyIndex < 0) {
    BaseMessage.warning('最多只能对比 3 款商品')
    return
  }

  nextProducts[emptyIndex] = product
  selectedPkProducts.value = nextProducts
  trackClickEvent({
    ...buildProductClickPayload({
      pvEvent: categoryPkEvents.value.addComparison,
      pageName: pageTitle.value,
      product,
      targetType: 'category_pk_product',
      trackContext: categoryProductTrackContext.value,
    }),
  })
  void ensurePkProductParameters(nextProducts)
}

/**
 * @description 从 PK 区移除非基准商品，保留空状态列
 * @param {string|number} productId - 商品 ID
 * @returns {void}
 */
function removeProductFromPk(productId) {
  if (shouldUseAutoPkOnly.value) return
  const firstProduct = selectedPkProducts.value[0]
  if (String(getProductId(firstProduct)) === String(productId)) return
  const removedProduct = selectedPkProducts.value.find(
    (product) => String(getProductId(product)) === String(productId)
  )
  selectedPkProducts.value = selectedPkProducts.value.map((product) =>
    String(getProductId(product)) === String(productId) ? null : product
  )
  if (removedProduct) {
    trackClickEvent({
      ...buildProductClickPayload({
        pvEvent: categoryPkEvents.value.removeComparison,
        pageName: pageTitle.value,
        product: removedProduct,
        targetType: 'category_pk_product',
        trackContext: categoryProductTrackContext.value,
        rememberEntry: false,
      }),
    })
  }
}

/**
 * @description 加入购物车
 * @param {Object} product - 商品数据
 * @returns {void}
 */
function handleAddCart(product) {
  if (userStore.guardStoreOrderOrNotify(isMobile.value)) return
  try {
    cartStore.addToCart({
      ...buildListAddToCartPayload({
        ...product,
        quantity: 1,
      }),
      selectAvailableSku: true,
      track: buildAddToCartTrack({
        eventName: categoryPkEvents.value.addToCart,
        pageName: pageTitle.value,
        fromPage: 'category_homepage',
        product,
        trackContext: categoryProductTrackContext.value,
      }),
    })
  } catch (error) {
    BaseMessage.error('加入购物车失败')
  }
}

/**
 * @description 跳转商品详情进行购买
 * @param {Object} product - 商品数据
 * @returns {void}
 */
function handleBuy(product) {
  if (userStore.guardStoreOrderOrNotify(isMobile.value)) return
  const productId = getProductId(product)
  if (!productId) return
  trackClickEvent({
    ...buildProductClickPayload({
      pvEvent: categoryPkEvents.value.buyNow,
      pageName: pageTitle.value,
      product,
      targetType: 'category_pk_product',
      trackContext: categoryProductTrackContext.value,
    }),
  })
  navigateTo(`/store/products/${productId}`)
}

/**
 * @description 二级分类切换：页面内更新，不跳转路由
 * @param {string} categoryId - 二级分类 ID
 */
async function handleSecondaryCategoryChange(categoryId) {
  if (String(activeSecondaryCategoryId.value) === String(categoryId)) return
  activeSecondaryCategoryId.value = categoryId
  replaceSecondaryCategoryQuery(categoryId)
  await refreshCategoryProductsOnly()
}

activeSecondaryCategoryId.value = resolveActiveSecondaryCategoryId(route.query.secondaryCategoryId)

onServerPrefetch(async () => {
  if (!publicContentSsrEnabled.value) return
  if (userStore.isLoggedIn) return
  const requestConfig = createPublicContentSsrRequestConfig({ timeout: 3_000 })
  try {
    await categoryStore.fetchCategoryInfo({ authKey: 'guest', requestConfig })
    await refreshCategoryContent(requestConfig)
  } catch {
    loading.value = false
  }
})

// SSR 使用本机 TDC 的受限访客请求预取聚合页与 PK 商品；客户端挂载后补拉当前状态。
onMounted(() => {
  refreshCategoryContent({}, { preserveExisting: true })

  nextTick(() => {
    categoryCardExposure.observe('.category-pk-page__product-card[data-prod-id]', (el) => {
      const prodId =
        el?.dataset?.prodId ||
        el?.querySelector('[data-prod-id]')?.dataset?.prodId ||
        el?.querySelector('.product-list-card')?.dataset?.prodId
      if (!prodId) return
      const exposureKey = `category_card_${categoryTrackingPrefix.value}_${prodId}`
      if (hasExposedInPage(exposureKey)) return
      markExposedInPage(exposureKey)
      const product = displayProducts.value.find(
        (item) => String(item?.prodId ?? item?.id ?? '') === String(prodId)
      ) || { prodId, id: prodId }
      const fields = buildProductTrackFields(product)
      const sourceCtx = categoryProductTrackContext.value
      safeTrack({
        eventName: categoryPkEvents.value.productCardPv,
        eventType: 'impression',
        targetType: 'category_product_card',
        targetId: fields.targetId,
        targetName: fields.targetName,
        pageName: pageTitle.value,
        product,
        item: product,
        sourceType: sourceCtx.sourceType,
        sourceId: sourceCtx.sourceId,
        sourceName: sourceCtx.sourceName,
        properties: {
          ...fields.properties,
          source_type: sourceCtx.sourceType,
          source_id: sourceCtx.sourceId,
          source_name: sourceCtx.sourceName,
          from_page: sourceCtx.fromPage,
        },
      })
    })
    categoryCardExposure.rescan()
  })
})

watch([routeCategoryId, () => route.query.secondaryCategoryId], async () => {
  await refreshCategoryContent()
})
</script>

<template>
  <div class="category-pk-page">
    <StoreHomeBanner
      :display-page="DISPLAY_PAGE.CATEGORY_AGGREGATE"
      :category-id="categoryPageId"
      :tracking="categoryBannerTracking"
    />

    <div class="category-pk-page__content">
      <StoreHomeCategoryNav
        :categories-data="secondaryCategories"
        :active-id="activeSecondaryCategoryId"
        :parent-category-id="categoryPageId"
        :intercept-click="true"
        :tracking="categoryNavTracking"
        @category-click="handleSecondaryCategoryChange"
      />

      <main class="category-pk-page__main base-container">
        <h1 class="category-pk-page__title">
          {{ displayPageTitle }}
        </h1>

        <div v-if="aggregatePageLoadFailed && !loading" class="category-pk-page__load-error">
          <p>分类内容加载失败，请重试</p>
          <a-button type="primary" @click="retryCategoryContent">重新加载</a-button>
        </div>

        <template v-else>
          <section
            v-if="loading || shouldShowProductPicker"
            class="category-pk-page__products"
            aria-label="可选商品"
          >
            <template v-if="loading">
              <div class="category-pk-page__product-grid">
                <BaseSkeleton v-for="item in 4" :key="item" :height="370" />
              </div>
            </template>
            <template v-else-if="shouldShowProductPicker">
              <BaseCardCarousel
                :gap="16"
                :padding-top="isMobile ? 12 : 0"
                :padding-bottom="isMobile ? 10 : 26"
                show-scrollbar
                :always-show-scrollbar="isMobile"
                :scrollbar-thumb-width="isMobile ? '248px' : '338px'"
              >
                <div
                  v-for="(product, index) in displayProducts"
                  :key="getProductId(product)"
                  class="category-pk-page__product-card"
                  :data-prod-id="getProductId(product) || ''"
                >
                  <StoreCategoryPkProductCard
                    :product="product"
                    :is-anchor="isAnchorProduct(product, index)"
                    :selected="isProductSelected(getProductId(product))"
                    :show-compare-action="!shouldUseAutoPkOnly"
                    :track-context="categoryProductTrackContext"
                    :disabled="
                      !isProductSelected(getProductId(product)) &&
                      selectedPkProducts.every((item) => item)
                    "
                    @add="addProductToPk"
                    @remove="removeProductFromPk"
                  />
                </div>
              </BaseCardCarousel>
            </template>
          </section>

          <StoreCategoryPkComparisonTable
            :products="selectedPkProducts"
            :rows="parameterRows"
            :loading="loading || parameterLoading"
            :removable="!shouldUseAutoPkOnly"
            @remove="removeProductFromPk"
            @add-cart="handleAddCart"
            @buy="handleBuy"
          />
        </template>
      </main>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.category-pk-page {
  min-height: 100vh;
  background-color: $bg-color;

  &__content {
    background-color: $bg-color;
  }

  &__main {
    padding-bottom: 32px;
  }

  &__title {
    margin: 0 0 24px;
    color: $text-title-color;
    font-size: 32px;
    font-weight: $font-weight-medium;
    line-height: 40px;
  }

  &__load-error {
    display: flex;
    min-height: 320px;
    align-items: center;
    flex-direction: column;
    justify-content: center;
    gap: 16px;
    color: $text-subtitle-color;

    p {
      margin: 0;
    }
  }

  &__products {
    position: relative;
    max-width: 100%;
    margin-bottom: 26px;
    padding-bottom: 0;
  }

  &__product-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 16px;
  }

  &__product-card {
    width: 338px;
    flex: 0 0 338px;
  }
}

@include respond-to-max('lg') {
  .category-pk-page {
    &__product-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }
}

@include respond-to-max('lg') {
  .category-pk-page {
    background-color: $bg-white;

    &__content {
      background-color: $bg-white;
    }

    &__main {
      padding-bottom: 24px;
    }

    &__title {
      margin-bottom: 12px;
      font-size: 16px;
      line-height: 24px;
    }

    &__product-card {
      width: 248px;
      flex-basis: 248px;
      overflow: hidden;
      border-radius: 8px;
      background-color: $bg-white;
      box-shadow: 0 4px 12px rgba($color-black, 0.08);
    }

    &__products {
      max-width: none;
      margin-bottom: 0;
      padding-bottom: 18px;
    }
  }
}
</style>
